import { Form } from "@inertiajs/react"
import { Pencil } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { useOnline } from "@/hooks/use-online"
import { crewSegmentPath } from "@/routes"

/**
 * OKS-28 — Inline rename popover for the manage page table row.
 *
 * **Inertia `<Form>` only — Crew rule.** No shadcn Form, no
 * react-hook-form, no zod. PATCH to `crewSegmentPath(id)` with literal
 * `name="crew_segment[name]"` matches the BE strong-params signature
 * (`params.require(:crew_segment).permit(:name)`).
 *
 * **A11y** (UX Spec §6): popover focus trap is shadcn default; focus
 * moves into the Input on open via `autoFocus`; Esc cancels (closes
 * the popover); Enter submits.
 *
 * **Locked testids:**
 *   - `crew-segments-row-rename-trigger-{id}` — opener Pencil button
 *   - `crew-segments-row-rename-popover-{id}` — popover content
 *   - `crew-segments-row-rename-input-{id}`   — name Input
 *   - `crew-segments-row-rename-submit-{id}`  — submit Button
 *   - `crew-segments-row-rename-cancel-{id}`  — cancel Button
 */
export interface SegmentRenamePopoverProps {
  segment: { id: number; name: string }
}

export function SegmentRenamePopover({ segment }: SegmentRenamePopoverProps) {
  const { t } = useTranslation()
  const online = useOnline()
  const [open, setOpen] = useState(false)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <Button
            type="button"
            variant="ghost"
            size="icon"
            data-testid={`crew-segments-row-rename-trigger-${segment.id}`}
            aria-label={t("crew_segments.manage.action_rename")}
          >
            <Pencil className="size-4" aria-hidden="true" />
          </Button>
        }
      />
      <PopoverContent
        data-testid={`crew-segments-row-rename-popover-${segment.id}`}
        className="w-80"
      >
        <Form
          action={crewSegmentPath(segment.id)}
          method="patch"
          options={{ preserveScroll: true }}
          onSuccess={() => setOpen(false)}
        >
          {({ errors, processing }) => (
            <div className="flex flex-col gap-3">
              <Label htmlFor={`crew-segment-rename-${segment.id}`}>
                {t("crew_segments.rename.name_label")}
                <span className="text-destructive ml-0.5" aria-hidden="true">
                  *
                </span>
              </Label>
              <Input
                id={`crew-segment-rename-${segment.id}`}
                data-testid={`crew-segments-row-rename-input-${segment.id}`}
                name="crew_segment[name]"
                type="text"
                autoFocus
                aria-required="true"
                aria-invalid={Boolean(errors.name)}
                maxLength={50}
                defaultValue={segment.name}
                disabled={processing}
              />
              <InputError messages={errors.name} />
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  data-testid={`crew-segments-row-rename-cancel-${segment.id}`}
                  disabled={processing}
                  onClick={() => setOpen(false)}
                >
                  {t("crew_segments.rename.cancel")}
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  data-testid={`crew-segments-row-rename-submit-${segment.id}`}
                  disabled={processing || !online}
                >
                  {processing
                    ? t("crew_segments.rename.saving")
                    : t("crew_segments.rename.submit")}
                </Button>
              </div>
            </div>
          )}
        </Form>
      </PopoverContent>
    </Popover>
  )
}
